import { useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  Chip,
  Field,
  Icon,
  Stack,
  Tabs,
  Text,
  tokens,
  type IconName,
} from '@/design';
import { useDefenceVariant } from '@/design/hooks';
import { useViewerStore } from '@/state/useViewerStore';
import { PivotLink } from '@/components/pivots/PivotLink';

type Severity = 'info' | 'success' | 'warning' | 'error';

interface SupportItem {
  id: string;
  title: string;
  body: string;
  icon: IconName;
  severity: Severity;
  action?: string;
}

const NOTIFICATION_ITEMS: SupportItem[] = [
  {
    id: 'GVW-136',
    title: 'Offline tile mode',
    body: 'Base tiles are unavailable. Cached data remains visible where present.',
    icon: 'cloud_off',
    severity: 'warning',
    action: 'Retry connection',
  },
  {
    id: 'GVW-138',
    title: 'RLS policy denied one layer',
    body: 'A restricted layer was hidden because the active role cannot read it.',
    icon: 'block',
    severity: 'error',
    action: 'Open audit row',
  },
  {
    id: 'GVW-141',
    title: 'Connector credentials expire soon',
    body: 'Refresh the source credentials before the next scheduled sync.',
    icon: 'key',
    severity: 'warning',
    action: 'Review credentials',
  },
  {
    id: 'GVW-170',
    title: 'Copilot budget approaching limit',
    body: 'Session spend is near the configured threshold. Expensive calls require confirmation.',
    icon: 'payments',
    severity: 'info',
    action: 'Open budget details',
  },
];

const HELP_ITEMS: SupportItem[] = [
  {
    id: 'GVW-155',
    title: 'Keyboard shortcuts',
    body: 'Map: arrows pan, plus and minus zoom, L focuses layers. 3D: F looks at selected, R resets view.',
    icon: 'keyboard_arrow_up',
    severity: 'info',
  },
  {
    id: 'GVW-156',
    title: 'Version and build',
    body: 'GeoViewer Phase 2 build. Rendering stack: MapLibre, deck.gl, Cesium, Three.js, Potree plugin host.',
    icon: 'deployed_code',
    severity: 'info',
  },
  {
    id: 'GVW-157',
    title: 'System health',
    body: 'Catalog, tile, identity, and audit health are shown with non-color status cues.',
    icon: 'monitor_heart',
    severity: 'success',
  },
  {
    id: 'GVW-158',
    title: 'Docs and what is new',
    body: 'Open docs for layer loading, scenario compare, share links, and defence-variant behavior.',
    icon: 'article',
    severity: 'info',
  },
];

const APP_LINKS: SupportItem[] = [
  {
    id: 'GVW-110',
    title: 'Measurement plugin',
    body: 'Distance and area tools share the active selection model and audit trail.',
    icon: 'straighten',
    severity: 'info',
  },
  {
    id: 'GVW-111',
    title: 'Annotation plugin',
    body: 'Create map notes and review comments without leaving the workspace.',
    icon: 'edit_note',
    severity: 'info',
  },
  {
    id: 'GVW-114',
    title: 'Export plugin',
    body: 'Export map images, evidence packs, and layer manifests when policy allows.',
    icon: 'file_download',
    severity: 'info',
  },
  {
    id: 'GVW-120',
    title: 'Catalog browse',
    body: 'Browse STAC collections and add validated datasets to the current view.',
    icon: 'dataset',
    severity: 'info',
  },
];

const SETTINGS_ITEMS: SupportItem[] = [
  {
    id: 'GVW-145',
    title: 'Settings landing',
    body: 'Preferences, credentials, telemetry, notifications, and accessibility are grouped here.',
    icon: 'settings',
    severity: 'info',
  },
  {
    id: 'GVW-147',
    title: 'Layer credentials health',
    body: 'Shows expired, stale, and policy-blocked source credentials with recovery paths.',
    icon: 'vpn_key',
    severity: 'warning',
  },
  {
    id: 'GVW-150',
    title: 'Accessibility preferences',
    body: 'Density, reduced motion, focus visibility, and canvas narration preferences are honored.',
    icon: 'verified_user',
    severity: 'success',
  },
  {
    id: 'GVW-152',
    title: 'Diagnostics opt-in',
    body: 'Telemetry uses explicit opt-in and excludes sensitive layer payloads.',
    icon: 'monitoring',
    severity: 'info',
  },
];

const STATE_ITEMS: SupportItem[] = [
  {
    id: 'GVW-160',
    title: 'No organizations',
    body: 'The user has authenticated but no organization is assigned.',
    icon: 'business',
    severity: 'warning',
  },
  {
    id: 'GVW-162',
    title: 'Forbidden',
    body: 'Access is denied by role-level security or conditional access.',
    icon: 'lock',
    severity: 'error',
  },
  {
    id: 'GVW-167',
    title: 'Offline',
    body: 'Network-dependent surfaces degrade to cached data and local-only messaging.',
    icon: 'cloud_off',
    severity: 'warning',
  },
  {
    id: 'GVW-169',
    title: 'Share-link decode failed',
    body: 'Malformed, expired, or unsupported links are explained before returning to a safe default view.',
    icon: 'link_off',
    severity: 'error',
  },
];

function severityVariant(severity: Severity): 'info' | 'success' | 'warning' | 'error' {
  return severity;
}

function SupportList({
  title,
  description,
  items,
}: {
  title: string;
  description: string;
  items: SupportItem[];
}) {
  return (
    <Stack direction="column" gap={2}>
      <Text variant="body" as="p" style={{ margin: 0 }}>
        {description}
      </Text>
      <Stack direction="column" gap={2}>
        {items.map((item) => (
          <Card key={item.id} level={1} header={item.title} data-testid={`support-${item.id}`}>
            <Stack direction="column" gap={2}>
              <Stack direction="row" align="center" gap={1} style={{ flexWrap: 'wrap' }}>
                <Chip
                  icon={item.icon}
                  label={item.id}
                  variant={severityVariant(item.severity)}
                  aria-label={`${item.id} ${item.severity}`}
                />
                {item.action ? (
                  <Button variant="ghost" type="button">
                    {item.action}
                  </Button>
                ) : null}
                {/* Admin pivots for credential and permission errors */}
                {(item.id === 'GVW-141' || item.id === 'GVW-138') && (
                  <PivotLink
                    pivotId="admin-integrations"
                    context={{
                      extra: {
                        providerId: item.id === 'GVW-141' ? 'credential-provider' : 'rls-provider',
                      },
                    }}
                  />
                )}
              </Stack>
              <Text variant="body" as="p" style={{ margin: 0 }}>
                {item.body}
              </Text>
            </Stack>
          </Card>
        ))}
      </Stack>
      <Text variant="caption" as="p" style={{ margin: 0, color: tokens.color.ink.secondary }}>
        {title} uses persistent surfaces for errors and warnings; transient toasts are reserved for non-critical success
        and info states.
      </Text>
    </Stack>
  );
}

export function NotificationsPanel() {
  const [tab, setTab] = useState('inbox');
  const permissionDeniedMessage = useViewerStore((s) => s.permissionDeniedMessage);

  return (
    <Stack direction="column" gap={2}>
      {permissionDeniedMessage ? (
        <Callout severity="error" title="Permission denied">
          <Text variant="body" as="p" style={{ margin: 0 }}>
            {permissionDeniedMessage}
          </Text>
        </Callout>
      ) : null}
      <Tabs
        tabs={[
          { id: 'inbox', label: 'Inbox' },
          { id: 'warnings', label: 'Warnings' },
          { id: 'cost', label: 'Cost' },
        ]}
        value={tab}
        onChange={setTab}
        aria-label="Notification categories"
      />
      {tab === 'inbox' ? (
        <SupportList
          title="Notification inbox"
          description="Categorized workspace notices for offline, stale, RLS, quota, and credential states."
          items={NOTIFICATION_ITEMS}
        />
      ) : tab === 'warnings' ? (
        <SupportList
          title="Persistent warnings"
          description="Warnings remain visible until resolved or acknowledged, with icons and specific recovery actions."
          items={NOTIFICATION_ITEMS.filter((item) => item.severity === 'warning' || item.severity === 'error')}
        />
      ) : (
        <SupportList
          title="Quota and cost"
          description="Quota and budget messages use explicit meter copy and confirmations for expensive actions."
          items={NOTIFICATION_ITEMS.filter((item) => item.id === 'GVW-170')}
        />
      )}
    </Stack>
  );
}

export function HelpPanel() {
  return (
    <SupportList
      title="Help"
      description="Help surfaces expose shortcut maps, build information, system health, and docs without leaving the current view."
      items={HELP_ITEMS}
    />
  );
}

export function AppLauncherPanel() {
  return (
    <SupportList
      title="Plugins and catalog"
      description="App launcher entries expose Phase 2 plugin and catalog paths as policy-safe pivots."
      items={APP_LINKS}
    />
  );
}

export function UserSettingsPanel() {
  const [tab, setTab] = useState('settings');
  const defence = useDefenceVariant();

  return (
    <Stack direction="column" gap={2}>
      {defence.active ? (
        <Callout severity="warning" title="Defence variant local-only mode" icon="policy">
          <Text variant="body" as="p" style={{ margin: 0 }}>
            External providers and model services are blocked. Audit and local evidence surfaces remain available.
          </Text>
        </Callout>
      ) : null}
      <Tabs
        tabs={[
          { id: 'settings', label: 'Settings' },
          { id: 'states', label: 'State screens' },
          { id: 'audit', label: 'Audit' },
        ]}
        value={tab}
        onChange={setTab}
        aria-label="Account and settings sections"
      />
      {tab === 'settings' ? (
        <SupportList
          title="Settings"
          description="Settings collect app-level preferences and integration health without forking the platform shell."
          items={SETTINGS_ITEMS}
        />
      ) : tab === 'states' ? (
        <SupportList
          title="Standalone state screens"
          description="State screens explain empty, denied, offline, degraded, and decode-failure cases with direct recovery paths."
          items={STATE_ITEMS}
        />
      ) : (
        <SupportList
          title="Audit and reports"
          description="Audit rows, deep links, and evidence exports use stable identifiers and non-color severity cues."
          items={[
            {
              id: 'GVW-130',
              title: 'Session audit drawer',
              body: 'Lists layer changes, share events, copilot calls, and policy blocks for the current session.',
              icon: 'receipt_long',
              severity: 'info',
              action: 'Open session log',
            },
            {
              id: 'GVW-132',
              title: 'Evidence pack export',
              body: 'Exports a signed package when audit policy and role grants allow it.',
              icon: 'archive',
              severity: 'success',
              action: 'Prepare evidence pack',
            },
            {
              id: 'GVW-133',
              title: 'Full audit (platform admin)',
              body: 'Cross-app pivot to platform-admin with filtered audit context.',
              icon: 'open_in_new',
              severity: 'info',
              action: 'Open full audit',
            },
          ]}
        />
      )}
    </Stack>
  );
}

export function SearchAndCatalogPanel() {
  const catalog = useViewerStore((s) => s.catalog);
  const visible = useViewerStore((s) => s.visibleLayerIds);
  const setVisible = useViewerStore((s) => s.setVisible);

  return (
    <Stack direction="column" gap={2}>
      <Field label="Search catalog">
        {({ id }) => (
          <input
            id={id}
            type="search"
            placeholder="Search layers, STAC collections, places, and tools"
            aria-label="Search catalog"
            style={{
              width: '100%',
              minHeight: tokens.density.touchMin,
              border: tokens.border.thin,
              borderRadius: tokens.radius.input,
              background: tokens.color.surface[0],
              color: tokens.color.ink.primary,
              paddingInline: tokens.space[2],
              fontFamily: tokens.font.family.ui,
              fontSize: tokens.font.size.md,
            }}
          />
        )}
      </Field>
      <Callout severity="info" title="Catalog onboarding">
        <Text variant="body" as="p" style={{ margin: 0 }}>
          Browse, validate, upload, and track lineage for datasets before adding them to the active workspace.
        </Text>
      </Callout>
      <Stack direction="column" gap={2} role="list" aria-label="Catalog quick add">
        {catalog.slice(0, 5).map((layer) => {
          const active = visible.has(layer.id);
          return (
            <Card key={layer.id} level={1} header={layer.label ?? layer.id}>
              <Stack direction="row" align="center" justify="space-between" gap={2}>
                <Stack direction="row" align="center" gap={1} style={{ minWidth: 0 }}>
                  <Icon name="dataset" size={20} decorative />
                  <Text variant="caption" as="span" style={{ color: tokens.color.ink.secondary }}>
                    {layer.kind} / {layer.crs}
                  </Text>
                  {active ? <Badge label="Visible" variant="success" /> : null}
                </Stack>
                <Button type="button" variant={active ? 'secondary' : 'primary'} onClick={() => setVisible(layer.id, !active)}>
                  {active ? 'Remove' : 'Add'}
                </Button>
              </Stack>
            </Card>
          );
        })}
      </Stack>
    </Stack>
  );
}
